import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from "./dialog";
import { Button } from "./button";
import { Textarea } from "./textarea";

export interface TextEditDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Callback when the user saves with the new text */
  onSave: (text: string) => Promise<void>;
  /** The title of the dialog */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Placeholder text for the textarea */
  placeholder?: string;
  /** Initial value for the textarea */
  initialValue?: string;
  /** Label for the save button */
  saveLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Number of rows for the textarea */
  rows?: number;
  /** Size of the dialog */
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
}

/**
 * A reusable dialog component for editing text content.
 * Useful for notes, descriptions, comments, and other text editing scenarios.
 */
export function TextEditDialog({
  open,
  onClose,
  onSave,
  title,
  description,
  placeholder,
  initialValue = "",
  saveLabel = "Save",
  cancelLabel = "Cancel",
  rows = 5,
  size = "lg",
}: TextEditDialogProps) {
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);

  // Reset value when dialog opens with new initial value
  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(value);
      onClose();
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} size={size}>
      <DialogTitle>{title}</DialogTitle>
      {description && <DialogDescription>{description}</DialogDescription>}
      <DialogBody>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={isSaving}
          autoFocus
        />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleCancel} disabled={isSaving}>
          {cancelLabel}
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
